import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logoutUser, clear2FAState } from '../../store/slices/authSlice';
import FloatingSidebar from '../FloatingSidebar/FloatingSidebar';
import CurrencySelector from '../CurrencySelector/CurrencySelector';
import toast from 'react-hot-toast';

const NAV_LINKS = [
  { to: '/search-flight', label: 'Search Flight' },
  { to: '/who-we-are', label: 'Who We Are' },
  { to: '/what-we-do', label: 'What We Do' },
  { to: '/how-it-works', label: 'How It Works' },
];

const linkClass = (active) =>
  `text-[15px] sm:text-[16px] font-semibold block py-2.5 px-3 no-underline transition-colors duration-200 rounded-lg ${
    active ? 'text-[#5392f9] bg-blue-50/80' : 'text-[#737373] hover:text-[#5392f9] hover:bg-gray-50'
  }`;

const Header = ({ onLoginClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileClicked, setIsProfileClicked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const isJetSetter =
    user?.user_type?.toLowerCase() === 'jetsetter' ||
    user?.role?.toLowerCase() === 'jetsetter';

  const showAccountChrome =
    isAuthenticated && user && !location.pathname.toLowerCase().includes('email-verify');

  const isActive = (path) => location.pathname === path;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen((open) => !open);

  const toggleSidebar = () => setIsSidebarOpen((open) => !open);

  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logged out successfully');
      navigate('/');
    } catch {
      toast.error('Logout failed');
    }
  };

  const handleLoginClick = () => {
    sessionStorage.removeItem('twofa_email');
    sessionStorage.removeItem('twofa_password');
    sessionStorage.removeItem('twofa_questions');
    sessionStorage.removeItem('twofa_phone');
    dispatch(clear2FAState());
    navigate('/');
    closeMobileMenu();
    onLoginClick();
  };

  const openSidebarFromMobile = () => {
    closeMobileMenu();
    toggleSidebar();
  };

  const profileImage = user?.image_url || user?.image;
  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';

  const avatarEl = (
    <div className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
      {profileImage ? (
        <img
          src={profileImage}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
          }}
        />
      ) : (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      )}
    </div>
  );

  return (
    <header className="bg-white fixed top-0 left-0 right-0 z-[1000] shadow-sm border-b border-jet-border">
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-[70px] gap-3">
          <div className="flex-shrink-0 min-w-0">
            <Link to="/" className="inline-block" onClick={closeMobileMenu}>
              <img
                src="/jetscanner-logo.svg"
                alt="JetScanner360"
                className="h-7 sm:h-8 w-auto max-w-[160px] sm:max-w-none"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden md:flex flex-1 justify-center" aria-label="Main">
            <ul className="flex flex-row list-none gap-1 items-center m-0 p-0">
              {NAV_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={linkClass(isActive(to))}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {isAuthenticated && isJetSetter && (
              <div className="hidden md:block">
                <CurrencySelector />
              </div>
            )}

            {showAccountChrome ? (
              <>
                <div
                  className={`hidden md:flex items-center gap-2 py-2 px-2 rounded-xl cursor-pointer transition-all duration-200 border-4 ${
                    isProfileClicked ? 'border-[#ffd200] bg-[#fffef0]' : 'border-transparent'
                  }`}
                  onClick={() => setIsProfileClicked(!isProfileClicked)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setIsProfileClicked(!isProfileClicked);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {avatarEl}
                  <span
                    className={`text-[16px] font-semibold max-w-[140px] truncate ${
                      isProfileClicked ? 'text-[#5392f9]' : 'text-[#737373]'
                    }`}
                  >
                    {firstName.toUpperCase()} {lastName.toUpperCase()}
                  </span>
                </div>
                <button
                  id="sidebar-toggle"
                  type="button"
                  onClick={toggleSidebar}
                  className={`hidden md:flex items-center justify-center transition-all duration-200 cursor-pointer bg-transparent rounded-xl border-4 ${
                    isSidebarOpen ? 'border-[#ffd200] bg-[#fffef0]' : 'border-transparent'
                  }`}
                  aria-label="Toggle sidebar menu"
                  aria-expanded={isSidebarOpen}
                  style={{ minWidth: 50, minHeight: 50, padding: 8 }}
                >
                  <i className="fa fa-bars text-white bg-[#ffd200] w-[34px] h-[34px] leading-[34px] rounded-full text-base text-center inline-block" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleLoginClick}
                className="hidden md:block text-[16px] font-semibold py-2 px-4 rounded-md text-[#737373] hover:text-[#5392f9] bg-transparent border-none cursor-pointer whitespace-nowrap"
              >
                Login/Signup
              </button>
            )}

            {/* Mobile: quick sidebar access when logged in */}
            {showAccountChrome && (
              <button
                type="button"
                onClick={openSidebarFromMobile}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-[#ffd200] text-white shrink-0"
                aria-label="Open account menu"
              >
                <i className="fa fa-bars text-base" aria-hidden />
              </button>
            )}

            <button
              type="button"
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <span
                className={`block w-5 h-0.5 bg-jet-primary transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-[5px]' : ''
                }`}
              />
              <span
                className={`block w-5 h-0.5 bg-jet-primary my-1 transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0 scale-0' : ''
                }`}
              />
              <span
                className={`block w-5 h-0.5 bg-jet-primary transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-[5px]' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-14 sm:top-[70px] bottom-0 z-[999]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 border-0 cursor-default"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          />
          <nav
            className="relative bg-white border-b border-gray-200 shadow-lg max-h-full overflow-y-auto overscroll-contain"
            aria-label="Mobile main"
          >
            <ul className="flex flex-col list-none m-0 p-3 gap-0.5">
              {NAV_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={linkClass(isActive(to))} onClick={closeMobileMenu}>
                    {label}
                  </Link>
                </li>
              ))}

              {isAuthenticated && isJetSetter && (
                <li className="px-3 py-3 border-t border-gray-100 mt-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Currency
                  </p>
                  <CurrencySelector />
                </li>
              )}

              <li className="border-t border-gray-100 mt-1 pt-2">
                {showAccountChrome ? (
                  <button
                    type="button"
                    onClick={openSidebarFromMobile}
                    className="flex items-center gap-2 w-full text-left py-2.5 px-3 rounded-xl border-4 border-transparent hover:border-[#ffd200] hover:bg-[#fffef0] transition-all"
                  >
                    {avatarEl}
                    <span className="text-[15px] font-semibold text-[#737373] truncate min-w-0">
                      {firstName} {lastName}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleLoginClick}
                    className={`${linkClass(false)} w-full text-left bg-transparent border-none cursor-pointer`}
                  >
                    Login/Signup
                  </button>
                )}
              </li>

              {showAccountChrome && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      handleLogout();
                    }}
                    className="w-full text-left text-[15px] font-semibold py-2.5 px-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Log out
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}

      <FloatingSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
    </header>
  );
};

export default Header;
